import styled from "@emotion/styled";
import React, { useMemo } from "react";
import Input from "../../../../../UI/Form/Input/Input";
import Select from "../../../../../UI/Form/Select/Select";
import {
  aggregations,
  columnFormats,
} from "../../../../../utils/constants/constants";
import { omit, debounce } from "lodash-es";
import MathOperations from "./MathOperations";
import { getOverrideByName } from "../helper";
import Checkbox from "../../../../../UI/Form/Checkbox/Checkbox";

const Fields = styled.div`
  display: flex;
  justify-content: space-between;
`;
export function getAggregationPrefix(aggregation) {
  return (
    aggregations.find(
      (aggr) =>
        aggr.value === aggregation ||
        aggr.label === aggregation ||
        aggr.prefix === aggregation
    )?.prefix ?? ""
  );
}

export default function RegularColumn({
  overrides,
  selectedColumn,
  setChartState,
  setSelectedColumn,
  theme,
  query,
  rawRows,
  subTitles,
  fields,
  chart,
}) {
  const {
    column,
    subIndex,
    mapping,
    name,
    aggregation,
    index,
    isParameterized,
    hidden,
  } = selectedColumn;

  function getNestedFormat() {
    const { options } =
      columnFormats.find((category) =>
        category.options.find((format) => format.value === mapping?.type)
      ) ?? {};

    return options?.find((format) => format.value === mapping?.type);
  }

  const changeColumnAggregation = (option) => {
    function changeOverrideAggregation(override) {
      const { value } = option;
      const mathOperation = override.ops;

      if (!value) {
        return {
          ...omit(override, "aggregation"),
          ops: mathOperation ? omit(mathOperation, "aggregation") : null,
        };
      }

      return mathOperation
        ? {
            ...omit(override, "aggregation"),
            ops: { ...mathOperation, aggregation: value },
          }
        : { ...override, aggregation: value };
    }

    setChartState({
      ...chart,
      overrides: chart.overrides.map((override) =>
        override.name === name ? changeOverrideAggregation(override) : override
      ),
      ...(chart.subTitles && {
        subTitles: chart.subTitles.map((subTitle, i) =>
          subTitle.map((sub, j) => {
            if (subIndex === i && sub === column && j === index) {
              return option.prefix + name;
            }

            return sub;
          })
        ),
      }),
      ...(chart.dynamicSubTitleKeys && {
        dynamicSubTitleKeys: chart.dynamicSubTitleKeys.map((key) =>
          name === key ? option.prefix + name : key
        ),
      }),
    });

    setSelectedColumn((prev) => ({
      ...(!option.value
        ? omit(prev, "aggregation")
        : { ...prev, aggregation: option.value }),
      subIndex,
      column: option.prefix + name,
      index,
    }));
  };

  const changeColumnName = (option) => {
    const aggregationPrefix = getAggregationPrefix(option.aggregation);

    const isExist = getOverrideByName(overrides, option.name);

    setChartState({
      ...chart,
      ...(chart.subTitles && {
        subTitles: chart.subTitles.map((subTitle, i) =>
          subTitle.map((sub, j) => {
            if (subIndex === i && sub === column && j === index) {
              return aggregationPrefix + option.name;
            }

            return sub;
          })
        ),
      }),
      ...(chart.dynamicSubTitleKeys && {
        dynamicSubTitleKeys: chart.dynamicSubTitleKeys.map((key) =>
          name === key ? option.name : key
        ),
      }),
      ...(!isParameterized && {
        overrides: isExist
          ? chart.overrides.map((override) =>
              override.name === name ? option : override
            )
          : [...chart.overrides, option],
      }),
    });

    setSelectedColumn({
      ...selectedColumn,
      ...option,
      subIndex,
      column: aggregationPrefix + option.name,
      index,
      name: option.name,
    });
  };

  const changeDisplayNameWithDelay = useMemo(
    () =>
      debounce(
        (value) =>
          setChartState({
            ...chart,
            overrides: chart.overrides.map((override) =>
              override.name === selectedColumn.name
                ? {
                    ...override,
                    mapping: { ...override.mapping, displayName: value },
                  }
                : override
            ),
          }),
        500
      ),
    [selectedColumn.name, setChartState, chart]
  );

  const changeDisplayName = (e) => {
    // debounce need for see changes on fly in preview
    changeDisplayNameWithDelay(e.target.value);

    setSelectedColumn({
      ...selectedColumn,
      mapping: { ...selectedColumn.mapping, displayName: e.target.value },
    });
  };

  const changeFormat = (option) => {
    setChartState({
      ...chart,
      overrides: chart.overrides.map((override) =>
        override.name === name
          ? !option.value
            ? omit(override, "mapping.type")
            : {
                ...override,
                mapping: { ...override.mapping, type: option.value },
              }
          : override
      ),
    });

    setSelectedColumn({
      ...selectedColumn,
      mapping: { ...selectedColumn.mapping, type: option.value },
    });
  };

  const excludeFromTotals = () => {
    const noTotalCalcKeys = (chart.noTotalCalcKeys ?? []).includes(column)
      ? chart.noTotalCalcKeys.filter((key) => key !== column)
      : [...(chart.noTotalCalcKeys ?? []), column];

    setChartState({
      ...chart,
      noTotalCalcKeys,
    });
  };

  return (
    <>
      <Fields>
        <div style={{ width: isParameterized ? "100%" : "60%" }}>
          <Select
            options={fields}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.name}
            value={selectedColumn}
            onChange={changeColumnName}
            label="Column"
            isDisabled={rawRows && !subTitles}
            cy="select-column-name"
          />
        </div>
        {!isParameterized && (
          <div style={{ width: "35%" }}>
            <Select
              options={[{ label: "None", prefix: "" }, ...aggregations]}
              value={
                aggregations.find((aggr) => aggr.value === aggregation) || {
                  label: "None",
                  prefix: "",
                }
              }
              label="Aggregate"
              onChange={changeColumnAggregation}
              cy="select-column-aggregation"
            />
          </div>
        )}
      </Fields>

      {!hidden && (
        <hr color={theme.background.secondary} style={{ margin: "10px 0px" }} />
      )}

      {!hidden && (
        <Input
          label="Column Header Label"
          inPlaceEdit
          value={mapping?.displayName ?? ""}
          hideEditIcon
          onChange={changeDisplayName}
        />
      )}

      <hr color={theme.background.secondary} style={{ margin: "10px 0px" }} />

      <Select
        options={columnFormats}
        value={getNestedFormat()}
        onChange={changeFormat}
        label="Format"
      />

      <hr color={theme.background.secondary} style={{ margin: "10px 0px" }} />
      <MathOperations
        setChartState={setChartState}
        selectedColumn={selectedColumn}
        setSelectedColumn={setSelectedColumn}
        overrides={overrides}
        query={query}
        chart={chart}
      />

      <hr color={theme.background.secondary} style={{ margin: "10px 0px" }} />
      <Checkbox
        label="Exclude from Totals"
        checked={(chart.noTotalCalcKeys ?? []).includes(column)}
        onChange={excludeFromTotals}
      />
    </>
  );
}
